import React, { useLayoutEffect, useRef, useState } from 'react';

import { EMPTY_DISPLAY_VALUE } from '../../constants/formatting';
import { SortOrder } from '../../types';

import {
  TableContainer,
  ScrollContainer,
  ScrollOverflowCover,
  HeadCell,
  Arrow,
  BodyRow,
  BodyCell,
} from './Table.styled';
import { TableColumn, TableData } from './types';

interface TableProps<Data> extends React.HTMLAttributes<HTMLTableElement> {
  className?: string;
  columns: TableColumn[];
  data?: Data[];
  sortColumn?: keyof Data | null;
  sortDirection?: SortOrder | null;
  onSort?: (columnName: keyof Data, direction: SortOrder) => void;
  isScrollDisabled?: boolean;
  isScrollOverflowDisplayed?: boolean;
  stripedData?: string[];
}

interface StickyColumn {
  index: number;
  offsetLeft: number;
}

const Table = <Data extends TableData>({
  className,
  columns = [],
  data = [],
  sortColumn,
  sortDirection,
  onSort,
  isScrollDisabled,
  isScrollOverflowDisplayed = true,
  stripedData,
  ...rest
}: TableProps<Data>) => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [stickyColumns, setStickyColumns] = useState<StickyColumn[]>([]);

  const handleColumnClick = (
    event: React.PointerEvent<HTMLTableCellElement>,
  ) => {
    const { column, isSortable } = event.currentTarget.dataset;

    if (column && isSortable && onSort) {
      const isSameColumn = column === sortColumn;
      const nextDirection: SortOrder =
        isSameColumn && sortDirection === 'asc' ? 'desc' : 'asc';

      onSort(column as keyof Data, nextDirection);
    }
  };

  const getPreviousStickyOffset = (index: number) => {
    const targetIndex = stickyColumns.findIndex(
      column => column?.index === index,
    );

    return stickyColumns[targetIndex - 1]?.offsetLeft || 0;
  };

  useLayoutEffect(() => {
    let totalStickyWidth = 0;

    const newStickyColumns = columns.reduce(
      (stickyColumnsTotal: StickyColumn[], column, index) => {
        if (column.isSticky) {
          totalStickyWidth +=
            tableRef.current?.rows[0]?.cells[index]?.getBoundingClientRect()
              .width || 0;

          stickyColumnsTotal.push({
            index,
            offsetLeft: totalStickyWidth,
          });
        }

        return stickyColumnsTotal;
      },
      [],
    );

    setStickyColumns(newStickyColumns);
  }, [columns]);

  return (
    <TableContainer className={className}>
      <ScrollContainer $isScrollDisabled={isScrollDisabled} ref={scrollableRef}>
        <table {...rest} ref={tableRef}>
          <thead>
            <tr>
              {columns.map(
                (
                  {
                    isSortable,
                    isSticky,
                    key,
                    width,
                    label,
                    isLabelHidden = false,
                  },
                  index,
                ) => (
                  <HeadCell
                    $isSticky={isSticky}
                    $stickyOffset={getPreviousStickyOffset(index)}
                    data-column={key}
                    data-is-sortable={isSortable}
                    key={key}
                    width={width}
                    onClick={handleColumnClick}>
                    <span>
                      {!isLabelHidden && label}
                      {isSortable && (
                        <Arrow
                          $direction={sortDirection}
                          $isSorted={sortColumn === key}
                        />
                      )}
                    </span>
                  </HeadCell>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {data.map(row => (
              <BodyRow
                $bgColor={row.bgColor}
                $isStriped={stripedData?.some(id => row.id === id)}
                data-testid={`table-row-${row.id}`}
                key={row.id}>
                {columns.map(
                  (
                    {
                      key,
                      width,
                      isOverflowVisible,
                      isSticky,
                      isEmptyColumnPlaceholderOmmited = false,
                    },
                    index,
                  ) => (
                    <BodyCell
                      $isLastSticky={
                        stickyColumns[stickyColumns.length - 1]?.index === index
                      }
                      $isOverflowVisible={isOverflowVisible}
                      $isSticky={isSticky}
                      $stickyOffset={getPreviousStickyOffset(index)}
                      key={key}
                      width={width}>
                      {row[key] ||
                        (isEmptyColumnPlaceholderOmmited
                          ? ''
                          : EMPTY_DISPLAY_VALUE)}
                    </BodyCell>
                  ),
                )}
              </BodyRow>
            ))}
          </tbody>
        </table>
      </ScrollContainer>
      {isScrollOverflowDisplayed && <ScrollOverflowCover />}
    </TableContainer>
  );
};

export default Table;
